/**
 * Modulos contratables y sus dependencias.
 *
 * Un modulo habilita FUNCIONES COMERCIALES; nunca concede un permiso clinico.
 * La politica de autorizacion (ADR-004) evalua primero el permiso del rol y
 * despues si el modulo de la accion esta habilitado: el modulo solo puede
 * restar. Deshabilitar un modulo no borra ni vuelve ilegible lo que se creo
 * con el (principio 6 del plan maestro): solo impide crear cosas nuevas.
 *
 * Son CLAVES en ASCII: viajan en JSON, se guardan en la base y nombran
 * entitlements. El texto que ve la persona va en `NOMBRE_DE_MODULO`.
 */
export const MODULOS = [
    // Nucleo: lo que tiene cualquier consultorio.
    'expediente',
    'agenda',
    'documentos',
    // Especialidades.
    'ginecologia-colposcopia',
    'medicina-general',
    'cardiologia',
    'nutricion',
    'oftalmologia',
    'otorrinolaringologia',
    'gastroenterologia',
    'obesologia',
    // Servicios transversales.
    'estudios',
    'portal-paciente',
    'planes',
    'seguimiento',
    'mensajeria',
];
export const TIPOS_DE_MODULO = ['nucleo', 'especialidad', 'servicio'];
export const DEFINICION_DE_MODULO = {
    expediente: { tipo: 'nucleo', dependencias: [], implementado: true },
    agenda: { tipo: 'nucleo', dependencias: ['expediente'], implementado: true },
    documentos: { tipo: 'nucleo', dependencias: ['expediente'], implementado: true },
    'ginecologia-colposcopia': {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos'],
        implementado: true,
    },
    'medicina-general': {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos'],
        // Implementado desde F2.9: encuentro, mediciones, antecedentes y documento con
        // plantilla de punta a punta. El paquete sigue en borrador clinico (F6.4).
        implementado: true,
    },
    cardiologia: {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos', 'estudios'],
        // Implementado desde F3.5: consulta con plantilla, ECG con su original,
        // parametros opcionales, comparador y reporte firmado. Borrador clinico (F6.4).
        implementado: true,
    },
    nutricion: {
        tipo: 'especialidad',
        // El seguimiento (autocaptura, F4.7) es opcional para nutricion: el plan de
        // alimentacion funciona sin el.
        dependencias: ['expediente', 'documentos', 'planes'],
        // Implementado desde F4.6: valoracion, antropometria, plan de alimentacion,
        // plantillas y vista por dia. Borrador clinico (F6.4).
        implementado: true,
    },
    oftalmologia: {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos', 'estudios'],
        // Implementado desde F6.1: mediciones por ojo con metodo, plantilla con lado
        // fijo por campo, comparacion por ojo e imagenes como originales. Borrador
        // clinico (F6.4).
        implementado: true,
    },
    otorrinolaringologia: {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos', 'estudios'],
        // Implementado desde F6.2: plantilla por oido y region, audiometria
        // estructurada por oido y originales. Sin video: la cuarentena (F3.2) no lo
        // admite. Borrador clinico (F6.4).
        implementado: true,
    },
    gastroenterologia: {
        tipo: 'especialidad',
        dependencias: ['expediente', 'documentos', 'estudios'],
        // Implementado desde F6.3: procedimiento con preparacion y consentimiento,
        // muestras con localizacion, patologia vinculada a su muestra, pendientes en
        // la bandeja y firma antes de publicar. Borrador clinico (F6.4).
        implementado: true,
    },
    obesologia: {
        tipo: 'especialidad',
        // Como nutricion: el plan de alimentacion vive en planes; el seguimiento
        // (autocaptura) es opcional.
        dependencias: ['expediente', 'documentos', 'planes'],
        // Implementado desde F6.6 (decision del dueno, 2026-10-10): consulta con
        // metas individualizadas, mediciones con HbA1c, tratamiento y revision de
        // complicaciones. Borrador clinico (VALIDACION §9).
        implementado: true,
    },
    // Implementado desde F3.4: ordenes, muestras, estudios, resultado y publicacion.
    estudios: { tipo: 'servicio', dependencias: ['expediente'], implementado: true },
    // Implementado desde F4.2 a F4.4: cuenta, sesion, espacios y representacion.
    'portal-paciente': { tipo: 'servicio', dependencias: ['expediente'], implementado: true },
    // Implementado desde F4.5: planes versionados, programacion y ocurrencias.
    planes: { tipo: 'servicio', dependencias: ['expediente'], implementado: true },
    seguimiento: {
        tipo: 'servicio',
        dependencias: ['expediente', 'portal-paciente'],
        // Implementado desde F4.7: autocaptura, cuestionarios y bandeja de revision.
        implementado: true,
    },
    mensajeria: {
        tipo: 'servicio',
        dependencias: ['expediente', 'portal-paciente'],
        // Implementado desde F4.8: conversaciones clinicas y administrativas.
        implementado: true,
    },
};
/** Texto visible de cada modulo. */
export const NOMBRE_DE_MODULO = {
    expediente: 'Expediente clínico',
    agenda: 'Agenda',
    documentos: 'Recetas y notas',
    'ginecologia-colposcopia': 'Ginecología y colposcopía',
    'medicina-general': 'Medicina general',
    cardiologia: 'Cardiología',
    nutricion: 'Nutrición',
    oftalmologia: 'Oftalmología',
    otorrinolaringologia: 'Otorrinolaringología',
    gastroenterologia: 'Gastroenterología',
    obesologia: 'Obesidad y diabetes',
    estudios: 'Estudios y archivos',
    'portal-paciente': 'Portal del paciente',
    planes: 'Planes de atención',
    seguimiento: 'Seguimiento',
    mensajeria: 'Mensajería',
};
/**
 * Modulo del que depende cada permiso existente.
 *
 * Es el puente entre la matriz de roles de hoy y los modulos: la politica
 * pregunta «¿el rol tiene el permiso?» y luego «¿esta habilitado el modulo de
 * ese permiso?». `null` significa que el permiso no depende de ningun modulo
 * (administrar la cuenta del consultorio, leer la bitacora).
 */
export const MODULO_DE_PERMISO = {
    'organizacion:leer': null,
    'organizacion:editar': null,
    'usuarios:leer': null,
    'usuarios:administrar': null,
    'pacientes:leer': 'expediente',
    'pacientes:crear': 'expediente',
    'pacientes:editar': 'expediente',
    'pacientes:eliminar': 'expediente',
    'historia:leer': 'expediente',
    'historia:escribir': 'expediente',
    'encuentros:leer': 'expediente',
    'encuentros:escribir': 'expediente',
    'observaciones:leer': 'expediente',
    'observaciones:escribir': 'expediente',
    'antecedentes:leer': 'expediente',
    'antecedentes:escribir': 'expediente',
    'colposcopias:leer': 'ginecologia-colposcopia',
    'colposcopias:crear': 'ginecologia-colposcopia',
    'colposcopias:editar': 'ginecologia-colposcopia',
    'citas:leer': 'agenda',
    'citas:administrar': 'agenda',
    'agenda:configurar': 'agenda',
    'recetas:leer': 'documentos',
    'recetas:emitir': 'documentos',
    'notas:leer': 'documentos',
    'notas:escribir': 'documentos',
    'plantillas:leer': 'documentos',
    'plantillas:administrar': 'documentos',
    'documentos:leer': 'documentos',
    'documentos:escribir': 'documentos',
    // F4.9: compartir con la paciente es del portal.
    'documentos:compartir': 'portal-paciente',
    'estudios:leer': 'estudios',
    'estudios:escribir': 'estudios',
    'estudios:publicar': 'estudios',
    'planes:leer': 'planes',
    'planes:escribir': 'planes',
    'seguimiento:leer': 'seguimiento',
    'seguimiento:revisar': 'seguimiento',
    'mensajes:clinicos': 'mensajeria',
    'mensajes:administrativos': 'mensajeria',
    'documentos:supervisar': null,
    // F5.5: remitir dentro del consultorio y revisar credenciales.
    'pacientes:remitir': null,
    'credenciales:revisar': null,
    'auditoria:leer': null,
    'expediente:exportar': 'expediente',
};
/**
 * Lo que recibe un consultorio que ya existia antes de los modulos.
 *
 * La migracion de compatibilidad (F1.5) les concede exactamente esto para que
 * nadie pierda una funcion por la llegada de los entitlements.
 */
export const MODULOS_DE_COMPATIBILIDAD = [
    'expediente',
    'agenda',
    'documentos',
    'ginecologia-colposcopia',
];
/**
 * Paquetes comerciales ilustrativos (plan maestro §11.4). SIN precios: los
 * fija el dueno despues del piloto (supuesto S-03). Un paquete es una lista de
 * modulos base; las especialidades adicionales se contratan como extras.
 */
export const PAQUETES_COMERCIALES = ['consulta', 'seguimiento', 'clinica'];
export const MODULOS_DE_PAQUETE = {
    consulta: ['expediente', 'agenda', 'documentos'],
    seguimiento: [
        'expediente',
        'agenda',
        'documentos',
        'portal-paciente',
        'planes',
        'seguimiento',
        'mensajeria',
    ],
    clinica: [
        'expediente',
        'agenda',
        'documentos',
        'estudios',
        'portal-paciente',
        'planes',
        'seguimiento',
        'mensajeria',
    ],
};
/**
 * Especialidades que escriben planes de ALIMENTACION (F4.6, F6.6). Cualquiera
 * de ellas basta: un consultorio de obesidad y diabetes publica su tratamiento
 * nutricional sin contratar ademas nutricion.
 */
export const MODULOS_CON_ALIMENTACION = ['nutricion', 'obesologia'];
export function puedeEscribirAlimentacion(modulos) {
    return MODULOS_CON_ALIMENTACION.some((m) => modulos.includes(m));
}
/** Dependencias directas e indirectas de un modulo, sin repetir. */
export function dependenciasDe(modulo) {
    const vistas = new Set();
    const pendientes = [...DEFINICION_DE_MODULO[modulo].dependencias];
    while (pendientes.length > 0) {
        const actual = pendientes.pop();
        if (vistas.has(actual))
            continue;
        vistas.add(actual);
        pendientes.push(...DEFINICION_DE_MODULO[actual].dependencias);
    }
    return MODULOS.filter((m) => vistas.has(m));
}
/**
 * Dependencias que le faltan a un conjunto de modulos para ser valido.
 *
 * El alta y el cambio de entitlements lo usan para rechazar, antes de
 * guardar, una combinacion que no funcionaria (plan maestro §11.2, paso 3).
 */
export function dependenciasFaltantes(modulos) {
    const presentes = new Set(modulos);
    const faltan = new Set();
    for (const modulo of modulos) {
        for (const dependencia of dependenciasDe(modulo)) {
            if (!presentes.has(dependencia))
                faltan.add(dependencia);
        }
    }
    return MODULOS.filter((m) => faltan.has(m));
}
/** Ordena de modo que cada modulo vaya despues de sus dependencias. */
export function ordenarPorDependencias(modulos) {
    return [...modulos].sort((a, b) => dependenciasDe(a).length - dependenciasDe(b).length);
}
//# sourceMappingURL=modulos.js.map